import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { useNavigator } from '@repo/toolkit/use-navigator'
import AddressLink from '@repo/ui/address-link'
import EditableInnerBox from '@repo/ui/editable-box'
import { BuildingThreeIcon, InformationIcon, LinkTwoIcon } from '@repo/ui/icons'
import TextEditor from '@repo/ui/text-editor'
import TextInformation from '@repo/ui/text-information'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getTripAddress } from '../../../api/trip-addresses/get-trip-address'
import { getTripBookingDispatchPlaces } from '../../../api/trip-booking-dispatch-places/get-trip-booking-dispatch-places'
import { Paths } from '../../../lib/constants/path'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { useNativeCountryName } from '../../../lib/hooks/use-native-country-name'
import {
  AccomodationEnum,
  accomodationEnumMapping
} from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { TripTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { HostFamilyProperties } from '../../../lib/types/models/host-family/properties.host-family'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripBookingProperties } from '../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripStepProperties } from '../../../lib/types/models/trip-booking/properties.trip-step'
import { constructCenterPath } from '../../../views/centers/utils'
import InformationToolTip from '../../elements/information-tooltip'
import { CenterAddressCoursesLabel } from '../centers/shared/center-address-courses'

type TripBookingInformationProps = {
  text: string
  richText?: string
}

const hasRichTextContent = (value?: string) => !!value?.replace(/<[^>]*>/g, '').trim()

const TripBookingInformation = ({ text, richText }: TripBookingInformationProps) => {
  return (
    <div className="flex items-center gap-1">
      <Typography variant="label-medium">{text}</Typography>
      {hasRichTextContent(richText) && (
        <InformationToolTip
          align="center"
          contentVariant="secondary"
          contentClassName="p-0 border-none"
          icon={
            <InformationIcon width="24px" height="24px" color={tailwindColors.secondary.DEFAULT} />
          }
        >
          <div className="w-56">
            <TextEditor value={richText} readOnly hideControls />
          </div>
        </InformationToolTip>
      )}
    </div>
  )
}

const EnrollmentProfileCenterInformation = () => {
  const nativeCountryName = useNativeCountryName()
  const { enrollment } = useEnrollment()
  const { navigate } = useNavigator()

  const { data: tripBookingDispatchPlaces } = useQuery({
    queryKey: ['trip-booking-dispatch-places', enrollment.id],
    queryFn: () =>
      getTripBookingDispatchPlaces<'DISPATCH_PLACES'>({ filter: { enrollmentId: enrollment.id } }),
    enabled: !!enrollment.id
  })

  const inboundDispatchPlaces =
    tripBookingDispatchPlaces?.data.filter(
      (tbdp) => tbdp.directionality === TripStepTypeEnum.INBOUND
    ) ?? []

  const allerTrip =
    inboundDispatchPlaces.find(({ type }) => type === TripTypeEnum.SECONDARY) ??
    inboundDispatchPlaces.find(({ type }) => type === TripTypeEnum.PRIMARY)

  const outboundDispatchPlaces =
    tripBookingDispatchPlaces?.data.filter(
      (tbdp) => tbdp.directionality === TripStepTypeEnum.OUTBOUND
    ) ?? []

  const retourTrip =
    outboundDispatchPlaces.find(({ type }) => type === TripTypeEnum.SECONDARY) ??
    outboundDispatchPlaces.find(({ type }) => type === TripTypeEnum.PRIMARY)

  const allerTripSteps = TripBookingProperties.getAllTripSteps(
    allerTrip?.tripBooking,
    TripStepTypeEnum.INBOUND
  )
  const retourTripSteps = TripBookingProperties.getAllTripSteps(
    retourTrip?.tripBooking,
    TripStepTypeEnum.OUTBOUND
  )

  const returnTripDetails = retourTripSteps?.length
    ? TripStepProperties.getArrivalTripStepPlaceAndParentTime(retourTripSteps[0])
    : '-'
  const departureTripDetails = allerTripSteps?.length
    ? TripStepProperties.getDepartureTripStepPlaceAndParentTime(allerTripSteps[0])
    : '-'

  const { data: returnTripAddress } = useQuery({
    queryKey: ['trip-address', retourTripSteps?.[0]?.arrivalPlace?.id],
    queryFn: () => {
      if (!retourTripSteps?.[0]?.arrivalPlace?.id) return null
      return getTripAddress(retourTripSteps?.[0]?.arrivalPlace?.id)
    },
    enabled: !!retourTripSteps?.[0]?.arrivalPlace?.id
  })

  const { data: departureTripAddress } = useQuery({
    queryKey: ['trip-address', allerTripSteps?.[0]?.departurePlace?.id],
    queryFn: () => {
      if (!allerTripSteps?.[0]?.departurePlace?.id) return null
      return getTripAddress(allerTripSteps?.[0]?.departurePlace?.id)
    },
    enabled: !!allerTripSteps?.[0]?.departurePlace?.id
  })

  return (
    <div>
      <EditableInnerBox>
        <div className="flex items-center gap-2">
          <BuildingThreeIcon className="w-5 h-5" color={tailwindColors.blue.DEFAULT} />
          <Typography.Link
            variant="secondary-body"
            onClick={() => {
              const centerId = enrollment.enrollmentCenter?.center.id
              if (!centerId) return
              navigate(constructCenterPath(Paths.CENTER_GLOBAL_VIEW_PAGE, centerId))
            }}
          >
            {enrollment.enrollmentCenter?.center.name}
          </Typography.Link>
        </div>
        <div className="h-2" />

        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-6">
            {enrollment.enrollmentCenter ? (
              <CenterAddressCoursesLabel id={enrollment.enrollmentCenter?.center.id} />
            ) : (
              <TextInformation title="Adresse des cours" value={'-'} />
            )}

            <TextInformation
              title="Adresse du logement"
              component={
                <div className="flex items-center">
                  {enrollment.enrollmentCenter?.center.accomodation ===
                  AccomodationEnum.HOST_FAMILY ? (
                    !enrollment.enrollmentCenter?.hostFamily?.familyName ? (
                      '-'
                    ) : (
                      <>
                        <LinkTwoIcon className="w-5 h-5" color={tailwindColors.blue.DEFAULT} />
                        <Typography.Link
                          variant="tag"
                          className="text-blue"
                          onClick={() => {
                            if (!enrollment.enrollmentCenter?.hostFamily?.id) return
                            navigate(
                              Paths.OL_HOST_FAMILY_INFORMATION.replace(
                                ':id',
                                enrollment.enrollmentCenter?.hostFamily.id.toString()
                              )
                            )
                          }}
                        >
                          {HostFamilyProperties.getFamilyNameWithAddress(
                            enrollment.enrollmentCenter.hostFamily
                          )}
                        </Typography.Link>
                      </>
                    )
                  ) : (
                    <AddressLink
                      address1={enrollment.enrollmentCenter?.center.journeyPlace?.name ?? ''}
                      country={
                        nativeCountryName(
                          enrollment.enrollmentCenter?.center.journeyPlace?.country.name
                        ) ?? ''
                      }
                    />
                  )}
                </div>
              }
            />
            <TextInformation
              title="Type d'hébergement"
              value={valueRender(
                enrollment.enrollmentCenter?.center.accomodation,
                (value) => accomodationEnumMapping[value]
              )}
            />
          </div>

          <div className="grid grid-cols-4 gap-6">
            <TextInformation
              title="Voyage principal"
              value={TripProperties.getTripLabel(enrollment.mainProposition?.mainTrip)}
            />
            <TextInformation
              title="Voyage secondaire"
              value={valueRender(
                TripProperties.getTripLabel(enrollment.mainProposition?.secondaryTrip),
                (value) => (value.length > 0 ? value : '-')
              )}
            />
            {enrollment.mainProposition?.mainTrip?.hasTransport && (
              <TextInformation
                title={
                  <>
                    <span className="text-secondary">Aller</span> - Lieu et heure de RDV parents
                  </>
                }
                component={
                  <TripBookingInformation
                    text={departureTripDetails}
                    richText={departureTripAddress?.comments}
                  />
                }
              />
            )}

            {enrollment.mainProposition?.mainTrip?.hasTransport && (
              <TextInformation
                title={
                  <>
                    <span className="text-green">Retour</span> - Lieu et heure de RDV parents
                  </>
                }
                component={
                  <TripBookingInformation
                    text={returnTripDetails}
                    richText={returnTripAddress?.comments}
                  />
                }
              />
            )}
          </div>
        </div>
      </EditableInnerBox>
    </div>
  )
}

export default EnrollmentProfileCenterInformation
